import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { ROLE_LABEL } from '../data/types';
import { useChargePoints } from '../lib/queries';
import { ICONS } from './icons';
import { freeNow } from '../data/types';

/** Menu grouped by area. Eleven flat items was a wall; the categories make the
 *  console scannable, and a group with nothing visible in it hides itself. */
const GROUPS: Array<{ cat: string; items: Array<{ to: string; key: string; label: string }> }> = [
  { cat: 'Overview', items: [{ to: '/', key: 'dashboard', label: 'Dashboard' }] },
  {
    cat: 'Network',
    /* Three levels, in the order they contain each other. A station is the
       site; a charge point is a unit standing on it; a connector is one bay on
       that unit. The menu used to call a charge point a station and a connector
       a charger, which is how an operator ends up unable to find a charger that
       is plainly online. */
    items: [
      { to: '/stations', key: 'stations', label: 'Stations' },
      { to: '/chargepoints', key: 'chargepoints', label: 'Charge points' },
      { to: '/connectors', key: 'connectors', label: 'Connectors' },
      { to: '/testbench', key: 'testbench', label: 'Test a charge' },
      { to: '/ocpp', key: 'ocpp', label: 'OCPP messages' },
    ],
  },
  {
    cat: 'People',
    items: [
      { to: '/drivers', key: 'drivers', label: 'Drivers' },
      { to: '/agents', key: 'agents', label: 'Agents' },
    ],
  },
  {
    cat: 'Revenue',
    items: [
      { to: '/transactions', key: 'transactions', label: 'Transactions' },
      { to: '/topups', key: 'topups', label: 'Top-ups' },
      { to: '/agent-float', key: 'topups', label: 'Agent float' },
      { to: '/documents', key: 'drivers', label: 'Documents' },
      { to: '/vehicle-documents', key: 'drivers', label: 'Vehicle papers' },
      { to: '/connector-types', key: 'chargepoints', label: 'Connector types' },
      { to: '/charger-models', key: 'chargepoints', label: 'Charger models' },
      { to: '/tariffs', key: 'tariffs', label: 'Tariffs' },
      { to: '/disputes', key: 'disputes', label: 'Disputes' },
      { to: '/reports', key: 'reports', label: 'Usage report' },
    ],
  },
  {
    cat: 'System',
    items: [
      { to: '/users', key: 'users', label: 'Users & roles' },
      { to: '/releases', key: 'releases', label: 'App releases' },
      { to: '/maps', key: 'maps', label: 'Maps, keys & email' },
      { to: '/legal', key: 'maps', label: 'Terms & privacy' },
      { to: '/activity', key: 'activity', label: 'Activity log' },
      { to: '/api-usage', key: 'reports', label: 'API usage' },
    ],
  },
];

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/stations': 'Stations',
  '/chargepoints': 'Charge points',
  '/connectors': 'Connectors',
  '/testbench': 'Test a charge',
  '/ocpp': 'Charger messages (OCPP)',
  '/drivers': 'Drivers',
  '/agents': 'Agents',
  '/transactions': 'Transactions',
  '/topups': 'Cash top-ups',
  '/agent-float': 'Agent float',
  '/documents': 'Driver documents',
  '/vehicle-documents': 'Vehicle documents',
  '/connector-types': 'Connector types',
  '/charger-models': 'Charger models',
  '/tariffs': 'Tariffs',
  '/disputes': 'Disputes & claims',
  '/reports': 'Usage report',
  '/users': 'Users & roles',
  '/releases': 'App releases',
  '/maps': 'Maps & API keys',
  '/activity': 'Activity log',
  '/api-usage': 'API usage',
  '/legal': 'Terms & privacy',
};

function OnlinePill() {
  const { data: res } = useChargePoints();
  const data = res?.items;
  if (!data) return null;
  /* This counts connectors, and used to be labelled "chargers" — which read as
     a charge point count and disagreed with every other tile on the console. */
  // A connector on a blocked station is not reachable however healthy the
  // hardware claims to be, so freeNow is the honest number here too.
  const total = data.reduce((n, s) => n + s.connectors.length, 0);
  const up = data.reduce((n, s) => n + freeNow(s) + s.connectors.filter((c) => c.status === 'charging').length, 0);
  const points = data.length;
  const onlinePoints = data.filter((s) => s.status !== 'offline' && !s.blocked).length;
  const tone = onlinePoints === points ? 'on' : onlinePoints ? 'mid' : 'off';
  return (
    <span className="pill" title={`${up} of ${total} connectors usable right now`}>
      <span className={`dot ${tone}`} />
      <span className="mono">{onlinePoints} / {points} charge points online</span>
    </span>
  );
}

export function Shell() {
  const { user, screens, signOut, theme, setTheme } = useSession();
  const { pathname } = useLocation();
  if (!user) return null;

  /* Straight from the token the server issued. A role change therefore takes
     effect on the next refresh rather than the next deploy, and the menu can
     never offer something the server would refuse. */
  const allowed = screens;

  return (
    <div className="shell">
      <aside className="side">
        <div className="brand">
          <div className="m">⚡</div>
          <div>
            <div className="n">Spark EV</div>
            <div className="r">Admin console</div>
          </div>
        </div>

        <nav className="nav">
          {GROUPS.map((g) => {
            const items = g.items.filter((i) => allowed.includes(i.key));
            // Support sees four of fourteen items; a heading with nothing under
            // it reads as a broken menu.
            if (!items.length) return null;
            return (
              <div className="nav-group" key={g.cat}>
                <div className="nav-cat">{g.cat}</div>
                {items.map((i) => (
                  <NavLink
                    key={i.key}
                    to={i.to}
                    end={i.to === '/'}
                    className="nav-link"
                    aria-current={pathname === i.to ? 'true' : undefined}
                  >
                    {ICONS[i.key]}
                    {i.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 9, marginTop: 9 }}>
          <div className="nav-cat">Appearance</div>
          <div className="chips" style={{ padding: '0 4px' }}>
            {(['light', 'dark', 'color', 'scifi'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className="chip"
                aria-pressed={theme === t}
                onClick={() => setTheme(t)}
                style={{ fontSize: 10.5, padding: '3px 8px' }}
              >
                {t === 'light' ? 'White' : t === 'dark' ? 'Black' : t === 'color' ? 'Colour' : 'Sci-Fi'}
              </button>
            ))}
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div>
            <div className="title-line">
              <span className="title">{TITLES[pathname] ?? 'Spark EV'}</span>
              <OnlinePill />
            </div>
            <div className="sub">Spark EV operations · Addis Ababa</div>
          </div>
          <div className="topright">
            <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
              <div style={{ fontWeight: 650, fontSize: 13 }}>{user.name}</div>
              <div className="li-sub">{ROLE_LABEL[user.role]}</div>
            </div>
            <button type="button" className="btn ghost sm" onClick={signOut}>Sign out</button>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
